import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import enLocale from '@fullcalendar/core/locales/en-gb';
import { useCalendarSessions } from '../hooks/useCalendarSessions';
import type { DatesSetArg, EventContentArg } from '@fullcalendar/core';
import type { EventClickArg } from '@fullcalendar/core';
import { Video, Phone, User } from 'lucide-react';
import { SessionStatus, SESSION_TYPE } from '../types/constants';
import { DashboardStats } from '../components/dashboard/DashboardStats';

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentViewStart, setCurrentViewStart] = useState<Date | null>(null);
  const [currentViewEnd, setCurrentViewEnd] = useState<Date | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { events, isLoading, error } = useCalendarSessions(
    currentViewStart || undefined,
    currentViewEnd || undefined
  );

  const handleDatesSet = (dateInfo: DatesSetArg) => {
    if (
      dateInfo.start.getTime() !== currentViewStart?.getTime() ||
      dateInfo.end.getTime() !== currentViewEnd?.getTime()
    ) {
      console.log('Updating view dates:', dateInfo.start, dateInfo.end);
      setCurrentViewStart(dateInfo.start);
      setCurrentViewEnd(dateInfo.end);
    }
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const sessionId = clickInfo.event.id;
    const patientId = clickInfo.event.extendedProps.patientId;

    if (sessionId && patientId) {
      navigate(`/patients/${patientId}/sessions/${sessionId}`);
    } else {
      console.error('Missing session or patient ID for calendar event');
    }
  };

  const renderEventContent = (eventInfo: EventContentArg) => {
    const { timeText, event, view } = eventInfo;
    const { status, type } = event.extendedProps as {
      status: SessionStatus;
      type: SessionType;
    };
    const patientName = event.title.split(' - ')[0];
    const TypeIcon =
      type === SESSION_TYPE.VIDEO
        ? Video
        : type === SESSION_TYPE.PHONE
        ? Phone
        : User;

    if (view.type === 'dayGridMonth') {
      return (
        <div className="flex items-center gap-1.5 px-1.5 py-0.5 truncate w-full">
          <TypeIcon className="w-3 h-3 shrink-0" />
          <span className="text-xs font-medium truncate">{patientName}</span>
        </div>
      );
    }

    return (
      <div className="flex flex-col p-1.5 h-full overflow-hidden">
        <div className="text-xs font-medium opacity-80 mb-px">{timeText}</div>
        <div className="flex items-center gap-1 mb-0.5">
          <span className="font-semibold text-sm truncate">{patientName}</span>
        </div>
        <div className="text-xs font-medium opacity-80">
          {t(`status.${status}`, status)}
        </div>
      </div>
    );
  };

  const currentLocale = i18n.language.startsWith('hu') ? huLocale : enLocale;

  return (
    <div>
      <h1 className="text-2xl font-semibold text-[#101828] mb-6">
        {t('dashboardPage.title')}
      </h1>

      <DashboardStats />
      
      <div className="bg-white rounded-xl shadow-sm border border-[#EAECF0] overflow-hidden relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-lg shadow-sm border border-[#EAECF0]">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#7F56D9] border-t-transparent"></div>
              <span className="text-[#344054] text-sm font-medium">
                {t('dashboardPage.loading')}
              </span>
            </div>
          </div>
        )}
        {error && !isLoading && (
          <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
        <style>
          {`
            .fc {
              --fc-border-color: #EAECF0;
              --fc-button-bg-color: #ffffff;
              --fc-button-border-color: #D0D5DD;
              --fc-button-text-color: #344054;
              --fc-button-hover-bg-color: #F9FAFB;
              --fc-button-hover-border-color: #D0D5DD;
              --fc-button-active-bg-color: #F5F6F7;
              --fc-button-active-border-color: #D0D5DD;
              --fc-today-bg-color: #F9F5FF;
              --fc-now-indicator-color: #7F56D9;
              font-family: inherit;
            }

            .fc .fc-button {
              padding: 0.5rem 1rem;
              font-weight: 500;
              font-size: 0.875rem;
              line-height: 1.25rem;
              border-radius: 0.5rem;
              height: auto;
              transition: all 0.2s;
            }

            .fc .fc-button-primary:not(:disabled).fc-button-active,
            .fc .fc-button-primary:not(:disabled):active {
              background: #F9F5FF;
              border-color: #7F56D9;
              color: #7F56D9;
              box-shadow: none;
            }

            .fc .fc-toolbar-title {
              font-size: 1.25rem;
              font-weight: 600;
              color: #101828;
            }

            .fc .fc-col-header-cell {
              padding: 0.75rem 0.5rem;
              font-weight: 500;
              color: #344054;
              background: #F9FAFB;
            }

            .fc .fc-timegrid-slot,
            .fc .fc-daygrid-day {
              height: 48px;
              border-color: #EAECF0;
            }

            .fc .fc-timegrid-slot-label {
              font-size: 0.75rem;
              color: #667085;
              font-weight: 500;
              text-transform: uppercase;
            }

            .fc .fc-event {
              border-radius: 0.375rem;
              border-width: 1px;
              box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
              cursor: pointer;
              padding: 0;
            }

            .fc .fc-event:hover {
              filter: brightness(0.95);
            }

            .fc .fc-daygrid-event {
              margin: 1px 4px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              background-color: transparent !important;
              border: none !important;
            }

            .fc .fc-toolbar.fc-header-toolbar {
              margin-bottom: 1.5rem;
              padding: 1rem;
              border-bottom: 1px solid #EAECF0;
            }

            .fc .fc-daygrid-day-events {
              margin-top: 1px;
              margin-bottom: 1px;
            }

            .fc .fc-daygrid-event-harness {
              margin-top: 1px;
              margin-bottom: 1px;
            }

            @media (max-width: 640px) {
              .fc .fc-toolbar {
                flex-direction: column;
                gap: 1rem;
              }

              .fc .fc-toolbar-title {
                font-size: 1rem;
              }
            }
          `}
        </style>
        {!error && (
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={windowWidth <= 640 ? 'timeGridDay' : 'timeGridWeek'}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right:
                windowWidth <= 640
                  ? 'timeGridDay'
                  : 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            locale={currentLocale}
            locales={[huLocale, enLocale]}
            weekends={true}
            events={events}
            datesSet={handleDatesSet}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            height="auto"
            allDaySlot={false}
            slotMinTime="08:00:00"
            slotMaxTime="20:00:00"
            nowIndicator={true}
            dayMaxEvents={true}
            eventMaxStack={3}
            slotDuration="00:30:00"
            slotLabelInterval="01:00"
            expandRows={true}
            stickyHeaderDates={true}
            firstDay={1}
          />
        )}
      </div>
    </div>
  );
};

export default DashboardPage;